import React, { useMemo } from 'react';
import DefaultTextField from 'components/Utils/DefaultTextField';
import DefaultSelectField from 'components/Utils/DefaultSelectField';
import { TimeInterval, TimeIntervalIntervalEnum } from 'generated/api/models/TimeInterval';

const ALL_INTERVALS: TimeIntervalIntervalEnum[] = [
  TimeIntervalIntervalEnum.Seconds,
  TimeIntervalIntervalEnum.Minutes,
  TimeIntervalIntervalEnum.Days,
  TimeIntervalIntervalEnum.Weeks,
  TimeIntervalIntervalEnum.Months,
  TimeIntervalIntervalEnum.Years,
];

interface TimeIntervalFieldProps {
  label?: string;
  value: TimeInterval | null;
  onChange: (value: TimeInterval | null) => void;
  style?: React.CSSProperties;
  valueWidth?: string;
  intervalWidth?: string;
  disabled?: boolean;
  min_interval?: TimeIntervalIntervalEnum;
}

const TimeIntervalField: React.FC<TimeIntervalFieldProps> = ({
  label,
  value,
  onChange,
  style = {},
  valueWidth = '70px',
  intervalWidth = '110px',
  disabled,
  min_interval = TimeIntervalIntervalEnum.Seconds,
}) => {
  const { intervalOptions, defaultInterval } = useMemo(() => {
    const minIndex = ALL_INTERVALS.indexOf(min_interval);
    const options = ALL_INTERVALS.slice(minIndex === -1 ? 0 : minIndex);
    const fallback = options.includes(TimeIntervalIntervalEnum.Months)
      ? TimeIntervalIntervalEnum.Months
      : options[0];
    return { intervalOptions: options, defaultInterval: fallback };
  }, [min_interval]);

  const intervalValue = value || { value: '', interval: defaultInterval };

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const num = e.target.value;
    if (num === '') {
      onChange(null);
      return;
    }
    onChange({
      value: parseInt(num, 10),
      interval: intervalValue.interval as TimeIntervalIntervalEnum,
    });
  };

  const handleIntervalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (intervalValue.value === '' || intervalValue.value == null) return;
    onChange({
      value: intervalValue.value as number,
      interval: e.target.value as TimeIntervalIntervalEnum,
    });
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', ...style }}>
      <DefaultTextField
        name="time_interval_value"
        label={label || ''}
        type="number"
        value={intervalValue.value ?? ''}
        onChange={handleValueChange}
        style={{ width: valueWidth }}
        placeholder="#"
        disabled={disabled}
      />
      <DefaultSelectField
        label=""
        value={intervalValue.interval}
        onChange={handleIntervalChange}
        inputs={intervalOptions}
        width={intervalWidth}
        disabled={disabled}
      />
    </div>
  );
};

export default TimeIntervalField;
